import * as React from "react";
import { QueryResult, graphql } from "react-apollo";
import gql from "graphql-tag";
import { FormikBag, Form, Formik } from "formik";
import Button from "@material-ui/core/Button";
import withOpenSnackbar, {
  OpenSnackbarFn,
} from "../../generic/snackbar/withOpenSnackbar";
import { CustomField } from "../../generic/form/index";
import FileInput from "../../generic/form/FileInput";
import { compose } from "recompose";
import getGraphqlException from "../../../../helpers/getGraphqlException";
import UploadSummaryLink from "../../generic/uploadSummary/UploadSummaryLink";
import { UploadSummary } from "../../generic/uploadSummary/UploadSummaryText";
import { SnackbarType } from "../../../../__generated__/globalTypes";
import { CloudUpload } from "@material-ui/icons";
import UploadFormSubmitContainer from "../../generic/uploads/UploadFormSubmitContainer";
import { organisationMarketplaceFragments } from "../../organisation/affiliations/marketplace/OrganisationMarketplaceAffiliations";

export const UPLOAD_MARKETPLACES = gql`
  mutation UploadCategoriesForMarketplace(
    $industryID: String!
    $file: Upload!
  ) {
    createMarketplacesForIndustryWithFile(
      industry: { id: $industryID }
      file: $file
    ) {
      createdCount
      updatedCount
      errors
    }
  }
`;

export const GET_MARKETPLACES = gql`
  query GetAvailableMarketplaces($industryID: String!) {
    industry: getIndustry(id: $industryID) {
      id
      marketplaceList {
        ...OrganisationMarketplace
      }
    }
  }
  ${organisationMarketplaceFragments.marketplace}
`;

type Props = {
  openSnackbar: OpenSnackbarFn;
  industryID: string;
  uploadMarketplaces: (
    marketplaceID: string,
    file: any
  ) => Promise<QueryResult<any>>;
};

type State = {
  uploadSummary?: UploadSummary;
};

type Values = {
  categoriesFile: any;
};

export class CategoryUploadForm extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      uploadSummary: undefined,
    };
  }

  async _handleSubmit(
    values: Values,
    { resetForm, setSubmitting }: FormikBag<any, Values>
  ) {
    const { uploadMarketplaces, openSnackbar, industryID } = this.props;

    if (industryID != null) {
      try {
        const successResult: QueryResult<any> = await uploadMarketplaces(
          industryID,
          values.categoriesFile
        );
        // Show snackbar on success
        openSnackbar(
          SnackbarType.success,
          `Successfully uploaded marketplaces`
        );
        setSubmitting(false);
        resetForm();
        // Store info about success
        this.setState({
          uploadSummary: {
            success:
              successResult &&
              successResult.data &&
              successResult.data.createMarketplacesForIndustryWithFile
                ? {
                    deleteCount:
                      successResult.data.createMarketplacesForIndustryWithFile
                        .deletedCount,
                    updateCount:
                      successResult.data.createMarketplacesForIndustryWithFile
                        .updatedCount,
                  }
                : undefined,
          },
        });
      } catch (error) {
        openSnackbar(SnackbarType.error, `Failed to upload marketplaces`);
        setSubmitting(false);
        // Store info about failure
        const exception = getGraphqlException(
          error,
          [`createMarketplacesForIndustryWithFile`],
          `BAD_USER_INPUT`
        );
        this.setState({
          uploadSummary:
            exception != null
              ? {
                  failure: {
                    badRowCount: exception.badRowCount || 0,
                    errors: exception.errors || [],
                    totalRowCount: exception.totalRowCount || 0,
                  },
                }
              : undefined,
        });
      }
    }
  }

  render() {
    const { industryID } = this.props;
    return (
      // @ts-ignore
      <Formik onSubmit={this._handleSubmit.bind(this)}>
        {(formikBag) => {
          const buttonIsDisabled =
            formikBag.isSubmitting ||
            !formikBag.dirty ||
            !formikBag.values.categoriesFile ||
            industryID == null;
          return (
            <Form>
              <CustomField
                fullWidth
                name="categoriesFile"
                component={FileInput}
                inputProps={{ accept: `.csv` }}
              />
              <UploadFormSubmitContainer>
                <Button
                  color="primary"
                  disabled={buttonIsDisabled}
                  startIcon={<CloudUpload />}
                  type="submit"
                  variant={"contained"}
                >
                  Upload
                </Button>
                <UploadSummaryLink
                  itemTitleText="Marketplace"
                  itemPluralText="marketplaces"
                  uploadSummary={this.state.uploadSummary}
                />
              </UploadFormSubmitContainer>
            </Form>
          );
        }}
      </Formik>
    );
  }
}

const enhancer = compose<Partial<Props>, any>(
  withOpenSnackbar as any,
  graphql(UPLOAD_MARKETPLACES, {
    props: ({ mutate }) => ({
      uploadMarketplaces: (industryID: string, file: any) =>
        mutate({
          refetchQueries: () => [
            {
              query: GET_MARKETPLACES,
              variables: {
                industryID,
              },
            },
          ],
          variables: {
            file,
            industryID,
          },
        }),
    }),
  })
);

export default enhancer(CategoryUploadForm);
